import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { gamePages, ImageItem } from '../gameConfig';
import ImageModal from './ImageModal';
import { useAudio } from '../hooks/useAudio';
import './GamePage.css';

const GamePage: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [selectedImage, setSelectedImage] = useState<ImageItem | null>(null);
  const [clickedIntruders, setClickedIntruders] = useState<string[]>([]);
  const [gameWon, setGameWon] = useState(false);
  const [revealedLetters, setRevealedLetters] = useState<string>('');
  const [showChicken, setShowChicken] = useState(false);
  const [chickenSide, setChickenSide] = useState(() => Math.random() > 0.5 ? 'right' : 'left');
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [viewedImages, setViewedImages] = useState<Set<string>>(new Set());
  const [showJumpscare, setShowJumpscare] = useState(false);
  const [hasTriggeredJumpscare, setHasTriggeredJumpscare] = useState(false);
  
  // Hook per Web Audio API
  const { unlocked, unlock, play } = useAudio('/pollo_sound.mp3');

  const currentGame = gamePages.find(page => page.id === gameId);
  
  // Randomize images order
  const shuffledImages = useMemo(() => {
    if (!currentGame) return [];
    return [...currentGame.images].sort(() => Math.random() - 0.5);
  }, [currentGame]);

  // Sblocca audio al primo tocco sulla pagina
  useEffect(() => {
    const firstTap = () => {
      unlock();
    };
    // pointerdown è più affidabile di click/touchend
    window.addEventListener('pointerdown', firstTap, { once: true, passive: true });
    return () => window.removeEventListener('pointerdown', firstTap);
  }, [unlock]);

  // Play sound when chicken appears (chiamato SOLO dentro il gesto)
  const playChickenSound = useCallback(() => {
    // offset 1s, durata 3s, volume 0.7
    play({ offset: 1, duration: 3, volume: 0.7 });
  }, [play]);

  useEffect(() => {
    // Show chicken after images are loaded
    if (imagesLoaded > 0 && imagesLoaded === shuffledImages.length) {
      // Show chicken initially
      setShowChicken(true);
      const hideTimer = setTimeout(() => {
        setShowChicken(false);
      }, 3000);

      // Set interval to show chicken every 15 seconds
      const intervalId = setInterval(() => {
        // Randomize side each time
        setChickenSide(Math.random() > 0.5 ? 'right' : 'left');
        setShowChicken(true);
        setTimeout(() => {
          setShowChicken(false);
        }, 3000);
      }, 15000);

      return () => {
        clearTimeout(hideTimer);
        clearInterval(intervalId);
      };
    }
  }, [imagesLoaded, shuffledImages.length]);

  const handleImageLoad = useCallback(() => {
    setImagesLoaded(prev => prev + 1);
  }, []);

  const handleImageClick = useCallback((image: ImageItem) => {
    setSelectedImage(image);
    
    // Track viewed images
    setViewedImages(prev => {
      const newSet = new Set(prev);
      newSet.add(image.id);
      return newSet;
    });
    
    if (image.isIntruder) {
      if (!clickedIntruders.includes(image.id)) {
        setClickedIntruders([...clickedIntruders, image.id]);
      }
    } else {
      setClickedIntruders([]);
    }
  }, [clickedIntruders]);

  const handleChickenClick = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Siamo in un gesto utente -> ok per suonare
    if (!unlocked) unlock(); // Doppia sicurezza
    playChickenSound();
    // Hide chicken after click and sound
    setTimeout(() => {
      setShowChicken(false);
    }, 3000);
  }, [playChickenSound, unlock, unlocked]);

  const handleCloseModal = useCallback(() => {
    setSelectedImage(null);
  }, []);

  const handleReset = useCallback(() => {
    setClickedIntruders([]);
    setGameWon(false);
    setRevealedLetters('');
  }, []);

  const handleJumpscareClick = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Quando l'utente tocca il jumpscare, siamo in un gesto: possiamo suonare
    if (!unlocked) {
      unlock();
      // Suona ora che abbiamo sbloccato
      play({ offset: 1, duration: 3, volume: 1 });
    }
    setTimeout(() => {
      setShowJumpscare(false);
    }, 3000);
  }, [unlock, unlocked, play]);

  useEffect(() => {
    if (clickedIntruders.length > 0 && currentGame) {
      const allIntrudersFound = 
        clickedIntruders.length === currentGame.intruderSequence.length &&
        currentGame.intruderSequence.every(intruder => clickedIntruders.includes(intruder));

      if (allIntrudersFound) {
        setGameWon(true);
        setRevealedLetters(currentGame.revealedLetters);
        
        // Letter saving removed - handled live
      }
    }
  }, [clickedIntruders, currentGame]);

  // Check if all images have been viewed for jumpscare
  useEffect(() => {
    if (currentGame && viewedImages.size === currentGame.images.length && !hasTriggeredJumpscare) {
      // Wait 2 seconds after the last image is viewed
      const timer = setTimeout(() => {
        setShowJumpscare(true);
        setHasTriggeredJumpscare(true);
        
        // Solo se già sbloccato, suona subito; altrimenti aspetta il tap sull'overlay
        if (unlocked) {
          play({ offset: 1, duration: 3, volume: 1 });
          // Chiudi dopo 3s se abbiamo suonato
          setTimeout(() => {
            setShowJumpscare(false);
          }, 3000);
        }
        // Se non sbloccato, l'utente chiuderà con un tap che farà partire l'audio
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [viewedImages, currentGame, hasTriggeredJumpscare, unlocked, play]);

  if (!currentGame) {
    return (
      <div style={{ padding: '20px', color: '#333', textAlign: 'center' }}>
        <h2>Pagina non trovata</h2>
        <p>ID richiesto: {gameId}</p>
      </div>
    );
  }

  return (
    <div className="game-container">
      {showChicken && (
        <div 
          className={`chicken-animation chicken-${chickenSide}`} 
          onPointerDown={handleChickenClick}
          style={{touchAction: 'manipulation'}}
        >
          <img src="/pollo.png" alt="" style={{cursor: 'pointer', pointerEvents: 'none'}} />
        </div>
      )}

      {showJumpscare && (
        <div 
          className="jumpscare-overlay" 
          onPointerDown={handleJumpscareClick}
          style={{touchAction: 'manipulation'}}
        >
          <img src="/pollo.png" alt="" className="jumpscare-chicken" />
        </div>
      )}

      {gameWon && (
        <div className="letters-popup-overlay" onClick={handleReset}>
          <div className="letters-popup-container">
            <img src="/fuochi.gif" alt="" className="fireworks-gif" />
            <div className="letters-popup">
              {revealedLetters}
            </div>
          </div>
        </div>
      )}

      <h1 style={{ 
        textAlign: 'center', 
        color: '#2c3e50', 
        fontSize: '2rem', 
        marginBottom: '20px',
        marginTop: '20px' 
      }}>
        {currentGame.title}
      </h1>

      <div className="images-grid">
        {shuffledImages.map((image) => (
          <div
            key={image.id}
            className="image-tile"
            onClick={() => handleImageClick(image)}
          >
            <img 
              src={image.src} 
              alt=""
              onLoad={handleImageLoad}
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/placeholder.jpg';
                handleImageLoad();
              }}
            />
          </div>
        ))}
      </div>

      {selectedImage && (
        <ImageModal
          image={selectedImage}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default GamePage;